import React, { FC, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { useGetAccountSetupProgressQuery } from "@store/accountSetupApi";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid2 as Grid,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link } from "react-router-dom";
import { setOpen } from "@store/inviteSlice";
import CloseIcon from "@mui/icons-material/Close";
import { setExpanded, setIsOpen, setProgress } from "@store/accountSetupSlice";

const MIN_PHOTO_COUNT = 10;
const MIN_FAMILY_MEMBER_COUNT = 5;

const Container = styled(Box)`
  background-color: rgb(248, 248, 248);
  position: fixed;
  border-radius: 4px;
  border-width: 1px;
  border-color: rgba(0, 0, 0, 0.08);
  border-style: solid;
  width: 320px;
  max-width: 100%;
  opacity: 1;
  z-index: 2;
  transition: all 225ms cubic-bezier(0.4, 0, 0.6, 1);
  max-height: calc(100dvh - 110px);
  overflow: auto;
`;

const ItemIcon: FC<{ complete: boolean }> = ({ complete }) => {
  return (
    <Box
      width="36px"
      height="36px"
      borderRadius="50%"
      bgcolor={complete ? "#2e7d32" : "#BDC1CAFF"}
      display="flex"
      justifyContent="center"
      alignItems="center"
      color={complete ? "white" : "#165044FF"}
    >
      {complete ? <CheckCircleOutlineIcon /> : <RadioButtonUncheckedIcon />}
    </Box>
  );
};

const ItemLink = styled(Link)`
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  color: #379ae6ff;
`;

interface ItemProps {
  title: string;
  subtitle?: string | (() => string);
  completed: boolean;
  link?: string;
  action?: () => void;
  value?: string | number;
}

const Item: FC<ItemProps> = ({
  title,
  subtitle,
  link,
  completed,
  value,
  action,
}) => {
  return (
    <Grid container spacing={2} alignItems="center">
      <Grid container size={10} spacing={1} alignItems="center">
        <Grid size={2}>
          <ItemIcon complete={completed} />
        </Grid>
        <Grid size={10}>
          <Stack>
            <ItemLink
              to={link || ""}
              {...(action && {
                onClick: (e) => {
                  e.preventDefault();
                  action();
                },
              })}
            >
              {title}
            </ItemLink>
            {subtitle && (
              <Typography
                fontSize="12px"
                lineHeight="20px"
                fontWeight="400"
                color="#171A1FFF"
              >
                {typeof subtitle === "function" ? subtitle() : subtitle}
              </Typography>
            )}
          </Stack>
        </Grid>
      </Grid>

      <Grid size={2} textAlign="right">
        <Typography
          fontSize="14px"
          lineHeight="22px"
          fontWeight="400"
          color="#379AE6FF"
        >
          {value || ""}
        </Typography>
      </Grid>
    </Grid>
  );
};

const AccountSetupProgress: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const { expanded, isOpen } = useSelector(
    (state: RootState) => state.accountSetup,
  );
  const { data, isLoading } = useGetAccountSetupProgressQuery(
    selectedPatientId!,
    {
      skip: !selectedPatientId,
    },
  );

  const openInviteDialog = useCallback(() => {
    dispatch(setOpen(true));
  }, [dispatch]);

  const items: ItemProps[] = useMemo(() => {
    if (!data) {
      return [];
    }
    return [
      {
        title: t("accountSetupProgress.patientProfile.title"),
        subtitle: () => {
          const missingItems = [];
          if (!data.patient.picture) {
            missingItems.push(t("accountSetupProgress.patientProfile.picture"));
          }
          if (!data.patient.occupations) {
            missingItems.push(
              t("accountSetupProgress.patientProfile.occupations"),
            );
          }
          if (!data.patient.residencies) {
            missingItems.push(
              t("accountSetupProgress.patientProfile.residencies"),
            );
          }
          return missingItems.length
            ? t("accountSetupProgress.patientProfile.subtitle", {
                items: missingItems.join(", "),
              })
            : t("accountSetupProgress.status.complete");
        },
        link: "/patient-info",
        completed:
          data.patient.picture &&
          !!data.patient.occupations &&
          !!data.patient.residencies,
      },
      {
        title: t("accountSetupProgress.familyMembers.title"),
        subtitle: t(
          data.patient.familyFriendsCount >= MIN_FAMILY_MEMBER_COUNT
            ? "accountSetupProgress.status.complete"
            : "accountSetupProgress.status.incomplete",
        ),
        link: "/patient-info/family-members",
        completed: data.patient.familyFriendsCount >= MIN_FAMILY_MEMBER_COUNT,
        value: data.patient.familyFriendsCount,
      },
      {
        title: t("accountSetupProgress.photos.title"),
        subtitle: t(
          data.gallery.photoCount >= MIN_PHOTO_COUNT
            ? "accountSetupProgress.status.complete"
            : "accountSetupProgress.status.incomplete",
        ),
        link: "/gallery/photos",
        completed: data.gallery.photoCount >= MIN_PHOTO_COUNT,
        value: data.gallery.photoCount,
      },
      {
        title: t("accountSetupProgress.faces.title"),
        subtitle:
          !data.gallery.pendingFacesCount && !!data.gallery.photoCount
            ? t("accountSetupProgress.status.complete")
            : t("accountSetupProgress.faces.subtitle"),
        link: "/gallery/faces",
        completed: !data.gallery.pendingFacesCount && !!data.gallery.photoCount,
        value: data.gallery.pendingFacesCount,
      },
      {
        title: t("accountSetupProgress.albums.title"),
        subtitle: !!data.gallery.albumCount
          ? t("accountSetupProgress.status.complete")
          : t("accountSetupProgress.albums.subtitle", {
              count: data.gallery.photoCount,
            }),
        link: "/gallery/albums",
        completed: !!data.gallery.albumCount,
        value: data.gallery.albumCount,
      },
      {
        title: t("accountSetupProgress.schedule.title"),
        subtitle: !!data.schedule.numberEvents
          ? t("accountSetupProgress.status.complete")
          : t("accountSetupProgress.schedule.subtitle"),
        link: "/schedule",
        completed: !!data.schedule.numberEvents,
        value: data.schedule.numberEvents,
      },
      {
        title: t("accountSetupProgress.invite.title"),
        action: openInviteDialog,
        completed: data.patient.invited,
      },
    ];
  }, [data, openInviteDialog, t]);

  const progress = useMemo(() => {
    if (!items.length) {
      return 0;
    }
    const completeItems = items.filter(({ completed }) => completed);
    return Math.round((completeItems.length / items.length) * 100);
  }, [items]);

  const dismiss = useCallback(() => {
    dispatch(setIsOpen(false));
  }, [dispatch]);

  const changeExpanded = useCallback(
    (expanded: boolean) => {
      dispatch(setExpanded(expanded));
    },
    [dispatch],
  );

  useEffect(() => {
    dispatch(setProgress(progress));
  }, [progress, dispatch]);

  if (isLoading || progress === 100 || !isOpen) {
    return null;
  }

  return (
    <Container bottom="36px" right="32px">
      <Box position="absolute" right="4px" top="4px" zIndex="2">
        <IconButton id="setup-account-popup-close-button" onClick={dismiss}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box padding="12px">
        <Box marginBottom="4px">
          <Typography
            fontSize="20px"
            lineHeight="30px"
            fontWeight="600"
            paddingRight="32px"
            color="#565D6DFF"
          >
            {t("accountSetupProgress.title")}
          </Typography>
        </Box>

        <Accordion
          disableGutters={true}
          variant="outlined"
          sx={{
            background: "transparent",
            border: "none",
            "&:before": { background: "none" },
          }}
          expanded={expanded}
          onChange={(_, expanded) => changeExpanded(expanded)}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              padding: 0,
              ".MuiAccordionSummary-content": {
                margin: 0,
                "&.Mui-expanded": { marginBottom: "24px" },
              },
            }}
          >
            <Box width="95%">
              <Typography
                textAlign="center"
                fontSize="20px"
                lineHeight="30px"
                fontWeight="600"
                marginBottom="4px"
                color="#565D6DFF"
              >
                {progress}%
              </Typography>

              <LinearProgress value={progress} variant="determinate" />
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ padding: 0 }}>
            <Stack gap="16px">
              {items.map((props) => (
                <Item key={props.title} {...props} />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Container>
  );
};

export default AccountSetupProgress;
